import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { EntryFormDialog } from '@/components/common/EntryFormDialog';
import { useUpdateJob } from '../hooks/useUpdateJob';
import { useProjectManagers } from '@/features/leads/hooks/useProjectManagers';
import { buildProjectManagerOptions } from '@/features/leads/utils/projectManagerOptions';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import type { JobDetailDto } from '../types';

interface JobEditFormProps {
  job: JobDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function JobEditForm({ job, isOpen, onClose, onSave }: JobEditFormProps) {
  const { hasPermission } = useAuth();
  const canEditCommission = hasPermission(Permissions.CommissionsUpdate);
  const canViewCommission = hasPermission(Permissions.CommissionsRead);
  const isEditable = job.status === 'Sold';

  // Track previous state to detect when to reset form (React-recommended pattern)
  const [prevIsOpen, setPrevIsOpen] = useState(false);
  const [prevJobId, setPrevJobId] = useState(job.id);
  const [description, setDescription] = useState(job.description || '');
  const [notes, setNotes] = useState(job.notes || '');
  const [assignedToUserId, setAssignedToUserId] = useState<number | undefined>(job.assignedToUserId ?? undefined);
  const [commissionRate, setCommissionRate] = useState<string>(job.commissionPercent?.toString() ?? '');
  const { updateJobDetails, isUpdating, error } = useUpdateJob();
  const { projectManagers } = useProjectManagers();

  // Merge a stranded assignee (missing from the fetched PM list, e.g.
  // role changed or deactivated) into the options so the current
  // assignment still displays instead of the placeholder.
  const projectManagerOptions = buildProjectManagerOptions(
    projectManagers,
    job.assignedToUserId,
    job.assignedTo,
  );

  // Reset form when dialog opens or job changes (during render, not in effect)
  if ((isOpen && !prevIsOpen) || job.id !== prevJobId) {
    setPrevIsOpen(isOpen);
    setPrevJobId(job.id);
    setDescription(job.description || '');
    setNotes(job.notes || '');
    setAssignedToUserId(job.assignedToUserId ?? undefined);
    setCommissionRate(job.commissionPercent?.toString() ?? '');
  } else if (!isOpen && prevIsOpen) {
    setPrevIsOpen(false);
  }

  const handleProjectManagerChange = (value: string) => {
    const newId = value ? Number(value) : undefined;
    setAssignedToUserId(newId);

    // Auto-populate commission rate from the project manager's default
    if (newId && canEditCommission) {
      const projectManager = projectManagers.find(pm => pm.id === newId);
      if (projectManager?.commissionRate != null) {
        setCommissionRate(projectManager.commissionRate.toString());
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = await updateJobDetails(job.id, {
      description: description || undefined,
      notes: notes || undefined,
      ...(isEditable && {
        assignedToUserId: assignedToUserId ?? null,
        ...(canEditCommission && commissionRate !== '' && {
          commissionRate: Number(commissionRate),
        }),
      }),
    });

    if (result) {
      onSave();
      onClose();
    }
  };

  const handleClose = () => {
    setDescription(job.description || '');
    setNotes(job.notes || '');
    setAssignedToUserId(job.assignedToUserId ?? undefined);
    setCommissionRate(job.commissionPercent?.toString() ?? '');
    onClose();
  };

  return (
    <EntryFormDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Job Details"
      error={error}
      isSubmitting={isUpdating}
      submitLabel="Save Changes"
      editLabel="Save Changes"
      isEditing={true}
      onSubmit={handleSubmit}
      maxWidth="sm:max-w-[600px]"
    >
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter job description..."
          rows={4}
          className="text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter job notes..."
          rows={4}
          className="text-sm"
        />
      </div>

      {isEditable && (
        <>
          <div className="space-y-2">
            <Label htmlFor="assignedTo">Project Manager</Label>
            <Select
              id="assignedTo"
              value={assignedToUserId?.toString() ?? ''}
              onChange={(e) => handleProjectManagerChange(e.target.value)}
            >
              <option value="">Select project manager</option>
              {projectManagerOptions.map((pm) => (
                <option key={pm.id} value={pm.id.toString()}>
                  {pm.name}
                </option>
              ))}
            </Select>
          </div>

          {canViewCommission && canEditCommission && (
            <div className="space-y-2">
              <Label htmlFor="commissionRate">Commission Rate (%)</Label>
              <Input
                id="commissionRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="0.00"
                className="text-sm"
              />
            </div>
          )}
        </>
      )}
    </EntryFormDialog>
  );
}
